/**
 * Работа хозяйства по предписанию — словами (журнал решений №659).
 *
 * Связь «находка → обязательство хозяйства → отчёт» уже лежит в базе
 * (`Obligation.q_finding`); сервер отдаёт её в `works` предписания. Отчёт
 * хозяйства значит «обработку провели», а НЕ «устранено»: устранение
 * подтверждает только повторный замер, и эти две оси здесь не сливаются.
 */
import type { Schemas } from '../../../api/client';
import { dateShort } from '../../lib/format';
import { OUTSIDE_CYCLE_WORD } from './quarantineArea';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  withOutside: { id: 'quarantine.work.withOutside', defaultMessage: '{base} · {outside}', description: 'Строка о работе хозяйства по предписанию с причиной «вне агроцикла»; {base} — основная фраза, {outside} — причина' },
  reportedEliminated: { id: 'quarantine.work.reportedEliminated', defaultMessage: 'хозяйство отчиталось об обработке {date} — устранение подтверждено замером', description: 'Строка о работе хозяйства по предписанию: отчёт получен и устранение подтверждено замером; {date} — дата отчёта' },
  reportedWaiting: { id: 'quarantine.work.reportedWaiting', defaultMessage: 'хозяйство отчиталось об обработке {date} — устранение замером ещё не подтверждено', description: 'Строка о работе хозяйства по предписанию: отчёт получен, но устранение замером не подтверждено (отчёт — не устранение); {date} — дата отчёта' },
  overdue: { id: 'quarantine.work.overdue', defaultMessage: 'хозяйство не отчиталось об обработке, срок работы прошёл', description: 'Строка о работе хозяйства по предписанию: отчёта нет, срок работы прошёл' },
  notReported: { id: 'quarantine.work.notReported', defaultMessage: 'хозяйство об обработке ещё не отчиталось', description: 'Строка о работе хозяйства по предписанию: отчёта пока нет, срок не прошёл' },
});

export type FindingWork = Schemas['QFindingWorkOut'];

/** Одна строка о работе хозяйства; `null` — работа хозяйству не назначалась. */
export function findingWorkText(works: ReadonlyArray<FindingWork> | undefined, eliminated: boolean): string | null {
  if (!works || works.length === 0) return null;
  const outside = works.find((w) => w.outside_cycle_reason)?.outside_cycle_reason;
  const base = baseWorkText(works, eliminated);
  return outside ? tr(m.withOutside, { base, outside: OUTSIDE_CYCLE_WORD[outside] ?? outside }) : base;
}

function baseWorkText(works: ReadonlyArray<FindingWork>, eliminated: boolean): string {
  const reported = works.filter((w) => w.reported_on);
  if (reported.length > 0) {
    const last = reported.map((w) => w.reported_on!).sort().at(-1)!;
    return tr(eliminated ? m.reportedEliminated : m.reportedWaiting, { date: dateShort(last) });
  }
  if (works.some((w) => w.status === 'overdue')) return tr(m.overdue);
  return tr(m.notReported);
}
